import { useEffect, useRef, useMemo } from 'react';

interface ParticleNode {
  x: number;
  y: number;
  size: number;
  type: 'transaction' | 'smartContract' | 'validator' | 'aiAgent';
  connections: number[];
  energy: number;
  pulsePhase: number;
  speed: number;
  angle: number;
  status: 'pending' | 'mining' | 'processed' | 'deciding' | 'confirmed' | 'analyzing' | 'trading';
  processingStage: number;
  data: {
    hash: string;
    value: number;
    strategy?: string;
    confidence?: number;
    profitLoss?: number;
    status?: 'training' | 'analyzing' | 'predicting';
    accuracy?: number;
    computeLoad?: number;
    lastUpdate?: number;
  };
  z: number;
  baseX: number;
  baseY: number;
  baseZ: number;
  velocity: { x: number; y: number; z: number };
  age: number;
  maxAge: number;
  trail: TrailPoint[];
  lastPosition: { x: number; y: number; z: number };
}

interface ParticleBackgroundProps {
  enhanced?: boolean;
  reducedMotion?: boolean;
  intensity?: 'low' | 'medium' | 'high';
  theme?: 'dark' | 'light' | 'neon';
}

interface ColorPalette {
  primary: string;
  secondary: string;
  pulse: string;
  gradient: string[];
}

interface ColorTheme {
  transaction: ColorPalette;
  smartContract: ColorPalette;
  validator: ColorPalette;
  aiAgent: ColorPalette;
}

interface IntensityConfig {
  particleCount: number;
  animationSpeed: number;
  connectionDistance: number;
}

interface InteractionZone {
  x: number;
  y: number;
  active: boolean;
  strength: number;
}

interface TrailPoint {
  x: number;
  y: number;
  z: number;
  opacity: number;
}

const ParticleBackground: React.FC<ParticleBackgroundProps> = ({ 
  enhanced = false, 
  reducedMotion = false,
  intensity = 'medium',
  theme = 'dark'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const frameCount = useRef(0);
  const animationRef = useRef<number>(0);
  const lastTime = useRef<number>(0);
  const isActive = useRef<boolean>(true);
  const interactionZone = useRef<InteractionZone>({
    x: 0, y: 0, active: false, strength: 0
  });
  
  // Performance optimization refs
  const nodesRef = useRef<ParticleNode[]>([]);
  const frameSkipCount = useRef<number>(0);

  // Generate a random hash-like string
  const generateHash = () => {
    return Array.from({ length: 8 }, () => 
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
  };

  // Enhanced color palette with theme support
  const colors = useMemo((): ColorTheme => {
    const baseColors: Record<string, ColorTheme> = {
      dark: {
        transaction: { primary: '#00ff8c', secondary: '#00cc71', pulse: '#00ff8c33', gradient: ['#00ff8c', '#00e676', '#00d463'] },
        smartContract: { primary: '#ff00ff', secondary: '#cc00cc', pulse: '#ff00ff33', gradient: ['#ff00ff', '#e100ff', '#d400ff'] },
        validator: { primary: '#00ffff', secondary: '#00cccc', pulse: '#00ffff33', gradient: ['#00ffff', '#00e5ff', '#00d0ff'] },
        aiAgent: { primary: '#ff3366', secondary: '#cc2952', pulse: '#ff336633', gradient: ['#ff3366', '#ff0055', '#e5004d'] }
      },
      light: {
        transaction: { primary: '#059669', secondary: '#047857', pulse: '#05966933', gradient: ['#059669', '#047857', '#065f46'] },
        smartContract: { primary: '#7c3aed', secondary: '#6d28d9', pulse: '#7c3aed33', gradient: ['#7c3aed', '#6d28d9', '#5b21b6'] },
        validator: { primary: '#0891b2', secondary: '#0e7490', pulse: '#0891b233', gradient: ['#0891b2', '#0e7490', '#155e75'] },
        aiAgent: { primary: '#dc2626', secondary: '#b91c1c', pulse: '#dc262633', gradient: ['#dc2626', '#b91c1c', '#991b1b'] }
      },
      neon: {
        transaction: { primary: '#00ff8c', secondary: '#00cc71', pulse: '#00ff8c66', gradient: ['#00ff8c', '#00e676', '#00d463'] },
        smartContract: { primary: '#ff00ff', secondary: '#cc00cc', pulse: '#ff00ff66', gradient: ['#ff00ff', '#e100ff', '#d400ff'] },
        validator: { primary: '#00ffff', secondary: '#00cccc', pulse: '#00ffff66', gradient: ['#00ffff', '#00e5ff', '#00d0ff'] },
        aiAgent: { primary: '#ff3366', secondary: '#cc2952', pulse: '#ff336666', gradient: ['#ff3366', '#ff0055', '#e5004d'] }
      }
    };
    
    return baseColors[theme] || baseColors.dark;
  }, [theme]);

  

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Set canvas size with higher pixel density
    const setCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Enhanced mouse interaction with smoother tracking
    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX;
      const newY = e.clientY;
      
      // Smooth mouse movement for better visual feedback
      mousePos.current = { 
        x: mousePos.current.x + (newX - mousePos.current.x) * 0.1,
        y: mousePos.current.y + (newY - mousePos.current.y) * 0.1
      };
      
      interactionZone.current = { 
        ...interactionZone.current, 
        x: newX, 
        y: newY,
        strength: interactionZone.current.active ? interactionZone.current.strength : 0
      };
    };
    
    const handleMouseDown = () => {
      interactionZone.current.active = true;
      interactionZone.current.strength = 1;
    };
    
    const handleMouseUp = () => {
      interactionZone.current.active = false;
    };
    
    const handleVisibilityChange = () => {
      isActive.current = document.visibilityState === 'visible';
    };

    // Throttle animation when tab is not visible
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchstart', (e) => {
      if (e.touches[0]) {
        handleMouseMove(e.touches[0] as unknown as MouseEvent);
        handleMouseDown();
      }
    }, { passive: true });
    window.addEventListener('touchend', handleMouseUp, { passive: true });

    // Intensity-based configuration
    const intensityConfig: Record<string, IntensityConfig> = {
      low: { particleCount: 0.5, animationSpeed: 0.5, connectionDistance: 0.7 },
      medium: { particleCount: 1, animationSpeed: 1, connectionDistance: 1 },
      high: { particleCount: 1.5, animationSpeed: 1.5, connectionDistance: 1.3 }
    };
    
    const config = intensityConfig[intensity];

    // Create initial nodes with improved parameters
    const createNode = (type: ParticleNode['type']): ParticleNode  => {
      // Improved 3D space distribution
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);  // Better spherical distribution
      const radius = Math.min(canvas.width, canvas.height) * (0.4 + Math.random() * 0.3);
      
      const baseNode: ParticleNode = {
        // Store base positions for animation
        baseX: radius * Math.sin(phi) * Math.cos(theta),
        baseY: radius * Math.sin(phi) * Math.sin(theta),
        baseZ: radius * Math.cos(phi),
        x: 0, // Will be calculated in animation
        y: 0,
        z: 0,
        size: type === 'validator' ? 12 : type === 'smartContract' ? 8 : 4,
        type,
        connections: [],
        energy: 0.5 + Math.random() * 0.5,
        pulsePhase: Math.random() * Math.PI * 2,
        speed: (type === 'validator' ? 0.2 : type === 'smartContract' ? 0.5 : 1) * config.animationSpeed,
        angle: theta,
        status: (['pending', 'mining', 'processed', 'deciding', 'confirmed'] as const)[Math.floor(Math.random() * 5)],
        processingStage: 0,
        data: {
          hash: generateHash(),
          value: Math.random() * 100
        },
        velocity: {
          x: (Math.random() - 0.5) * 0.1,
          y: (Math.random() - 0.5) * 0.1,
          z: (Math.random() - 0.5) * 0.1
        },
        age: 0,
        maxAge: 10000 + Math.random() * 5000, // 10-15 seconds
        trail: [],
        lastPosition: { x: 0, y: 0, z: 0 }
      };

      // Add AI-specific properties
      if (type === 'aiAgent') {
        return {
          ...baseNode,
          size: 10,
          speed: 0.3,
          data: {
            ...baseNode.data,
            strategy: ['Neural Network', 'Order Processor', 'Contract Auditor', 'Market Analyzer', 'Risk Manager', 'Pattern Detector'][Math.floor(Math.random() * 6)],
            confidence: Math.random(),
            profitLoss: (Math.random() * 2 - 1) * 100, // Range: -100 to +100
            status: (['training', 'analyzing', 'predicting'] as const)[Math.floor(Math.random() * 3)],
            accuracy: 0.5 + Math.random() * 0.5, // Range: 0.5 to 1.0
            computeLoad: Math.random(), // Range: 0 to 1
            lastUpdate: Date.now()
          }
        };
      }

      return baseNode as ParticleNode;
    };

    // Create more varied nodes with dynamic counts based on enhanced mode and intensity
    const baseTransactionCount = enhanced ? 25 : 15;
    const baseContractCount = enhanced ? 5 : 3;
    const baseValidatorCount = enhanced ? 3 : 2;
    const baseAiAgentCount = enhanced ? 8 : 5;
    
    const transactionCount = Math.floor(baseTransactionCount * config.particleCount + Math.random() * baseTransactionCount * 0.5);
    const contractCount = Math.floor(baseContractCount * config.particleCount + Math.random() * baseContractCount * 0.5);
    const validatorCount = Math.floor(baseValidatorCount * config.particleCount + Math.random() * baseValidatorCount * 0.5);
    const aiAgentCount = Math.floor(baseAiAgentCount * config.particleCount + Math.random() * baseAiAgentCount * 0.5);
    
    // Initialize nodes
    const nodes: ParticleNode[] = [
      ...Array.from({ length: transactionCount }, () => createNode('transaction')),
      ...Array.from({ length: contractCount }, () => createNode('smartContract')),
      ...Array.from({ length: validatorCount }, () => createNode('validator')),
      ...Array.from({ length: aiAgentCount }, () => createNode('aiAgent'))
    ];
    
    // Store nodes in ref for performance optimization
    nodesRef.current = nodes;

    // Draw particle trail
    const drawTrail = (node: ParticleNode): void => {
      if (node.trail.length < 2) return;
      
      const nodeColors = getNodeColors(node);
      
      for (let i = 0; i < node.trail.length - 1; i++) {
        const current = node.trail[i];
        const next = node.trail[i + 1];
        
        // Apply perspective to trail points
        const perspective = 1200;
        const scale1 = perspective / (perspective + current.z);
        const scale2 = perspective / (perspective + next.z);
        
        const x1 = canvas.width/2 + current.x * scale1;
        const y1 = canvas.height/2 + current.y * scale1;
        const x2 = canvas.width/2 + next.x * scale2;
        const y2 = canvas.height/2 + next.y * scale2;
        
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        
        ctx.strokeStyle = `${nodeColors.primary}${Math.floor(current.opacity * 255).toString(16).padStart(2, '0')}`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    };

    // Enhanced bloom effect for nodes
    const drawBloomEffect = (node: ParticleNode, nodeColors: ColorPalette): void => {
      if (reducedMotion) return;
      
      const bloomRadius = node.size * 3;
      const grd = ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, bloomRadius
      );
      grd.addColorStop(0, nodeColors.pulse.replace('33', '20'));
      grd.addColorStop(0.5, nodeColors.pulse.replace('33', '10'));
      grd.addColorStop(1, nodeColors.pulse.replace('33', '00'));

      ctx.beginPath();
      ctx.arc(node.x, node.y, bloomRadius, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();
    };

    // Draw cursor interaction effect
    const drawCursorEffect = (): void => {
      const cursorRadius = 50 + Math.sin(frameCount.current * 0.1) * 10;
      const grd = ctx.createRadialGradient(
        mousePos.current.x, mousePos.current.y, 0,
        mousePos.current.x, mousePos.current.y, cursorRadius
      );
      grd.addColorStop(0, 'rgba(255, 255, 255, 0.1)');
      grd.addColorStop(0.5, 'rgba(255, 255, 255, 0.05)');
      grd.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx.beginPath();
      ctx.arc(mousePos.current.x, mousePos.current.y, cursorRadius, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();

      // Inner ring
      ctx.beginPath();
      ctx.arc(mousePos.current.x, mousePos.current.y, 20, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 255, 255, ${interactionZone.current.strength * 0.3})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    };

    // Enhanced draw methods with gradients and effects
    const drawNode = (node: ParticleNode): void => {
      const pulse = Math.sin(node.pulsePhase + frameCount.current * 0.05) * 0.5 + 0.5;
      const nodeColors = getNodeColors(node);
      
      // Draw bloom effect first (behind the node)
      drawBloomEffect(node, nodeColors);
      
      // Enhanced pulse effect varies by status
      const pulseSize = node.status === 'mining' ? 3 + pulse * 2 : 
                        node.status === 'deciding' ? 2.5 + pulse * 1.5 : 
                        2 + pulse;

      // Energy wave with gradient
      const grd = ctx.createRadialGradient(
        node.x, node.y, node.size * 0.5,
        node.x, node.y, node.size * pulseSize
      );
      grd.addColorStop(0, nodeColors.pulse.replace('33', '66'));
      grd.addColorStop(1, nodeColors.pulse.replace('33', '00'));

      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size * pulseSize, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();

      // Draw node based on type
      if (node.type === 'aiAgent') {
        drawAIAgent(node, nodeColors);
      } else if (node.type === 'validator') {
        drawHexagon(node, nodeColors);
      } else if (node.type === 'smartContract') {
        drawDiamond(node, nodeColors);
      } else {
        drawCircle(node, nodeColors);
      }

      // Draw hash preview with improved styling
      if (node.type !== 'transaction') {
        // Drop shadow for text
        ctx.shadowColor = nodeColors.primary;
        ctx.shadowBlur = 5;
        ctx.font = 'bold 10px monospace';
        ctx.fillStyle = nodeColors.primary;
        ctx.fillText(node.data.hash.slice(0, 6), node.x - 15, node.y + node.size * 2);
        ctx.shadowBlur = 0;
      }

      // Enhanced display for AI agents
      if (node.type === 'aiAgent' && node.data.strategy) {
        // Status indicator with animation
        const statusBlink = node.data.status === 'analyzing' ? 
          (Math.sin(frameCount.current * 0.1) * 0.5 + 0.5) : 1;
          
        ctx.globalAlpha = statusBlink;
        ctx.font = '8px monospace';
        ctx.fillStyle = nodeColors.primary;
        ctx.fillText(node.data.strategy, node.x - 20, node.y - node.size * 1.5);
        
        // Confidence meter with profitLoss color
        if (node.data.confidence && node.data.profitLoss !== undefined) {
          const confidenceWidth = node.size * 2;
          const confidenceHeight = 3;
          const profitColor = node.data.profitLoss > 0 ? '#00ff8c' : '#ff3366';
          
          // Background bar
          ctx.fillStyle = '#333';
          ctx.fillRect(node.x - confidenceWidth/2, node.y + node.size * 1.5, confidenceWidth, confidenceHeight);
          
          // Fill bar
          ctx.fillStyle = profitColor;
          ctx.fillRect(
            node.x - confidenceWidth/2, 
            node.y + node.size * 1.5, 
            confidenceWidth * node.data.confidence, 
            confidenceHeight
          );
        }
        ctx.globalAlpha = 1;
      }
    };

    // Improved shape rendering with gradients
    const drawHexagon = (node: ParticleNode, colors: any) => {
      const grd = ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, node.size
      );
      grd.addColorStop(0, colors.gradient[0]);
      grd.addColorStop(0.7, colors.gradient[1]);
      grd.addColorStop(1, colors.gradient[2]);
      
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3;
        const x = node.x + node.size * Math.cos(angle);
        const y = node.y + node.size * Math.sin(angle);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      
      // Glow effect
      ctx.shadowColor = colors.primary;
      ctx.shadowBlur = 10;
      
      ctx.strokeStyle = colors.primary;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.fillStyle = grd;
      ctx.fill();
      
      ctx.shadowBlur = 0;
    };

    // Improved diamond with gradient
    const drawDiamond = (node: ParticleNode, colors: any) => {
      const grd = ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, node.size * 1.5
      );
      grd.addColorStop(0, colors.gradient[0]);
      grd.addColorStop(0.7, colors.gradient[1]);
      grd.addColorStop(1, colors.gradient[2]);
      
      ctx.beginPath();
      ctx.moveTo(node.x, node.y - node.size);
      ctx.lineTo(node.x + node.size, node.y);
      ctx.lineTo(node.x, node.y + node.size);
      ctx.lineTo(node.x - node.size, node.y);
      ctx.closePath();
      
      ctx.shadowColor = colors.primary;
      ctx.shadowBlur = 8;
      
      ctx.strokeStyle = colors.primary;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.fillStyle = grd;
      ctx.fill();
      
      ctx.shadowBlur = 0;
    };

    // Improved circle with gradient
    const drawCircle = (node: ParticleNode, colors: any) => {
      const grd = ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, node.size
      );
      grd.addColorStop(0, colors.gradient[0]);
      grd.addColorStop(0.7, colors.gradient[1]);
      grd.addColorStop(1, colors.gradient[2]);
      
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
      
      ctx.shadowColor = colors.primary;
      ctx.shadowBlur = 5;
      
      ctx.strokeStyle = colors.primary;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.fillStyle = grd;
      ctx.fill();
      
      ctx.shadowBlur = 0;
    };

    // Improved AI agent rendering
    const drawAIAgent = (node: ParticleNode, colors: any) => {
      const grd = ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, node.size * 1.5
      );
      grd.addColorStop(0, colors.gradient[0]);
      grd.addColorStop(0.6, colors.gradient[1]);
      grd.addColorStop(1, colors.gradient[2]);
      
      // Draw a dynamic pentagon for AI agents
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const pulseFactor = 1 + Math.sin(frameCount.current * 0.03 + i) * 0.1;
        const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
        const x = node.x + node.size * pulseFactor * Math.cos(angle);
        const y = node.y + node.size * pulseFactor * Math.sin(angle);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      
      ctx.shadowColor = colors.primary;
      ctx.shadowBlur = 10;
      
      ctx.strokeStyle = colors.primary;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.fillStyle = grd;
      ctx.fill();
      
      ctx.shadowBlur = 0;
      
      // Add a core in the center
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = colors.primary;
      ctx.fill();
    };

    // Enhanced connection rendering with data flow visualization
    const drawConnections = (node: ParticleNode, nodes: ParticleNode[]) => {
      nodes.forEach(other => {
        if (node === other) return;
        
        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const dz = other.z - node.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        
        // Adjust connection distance based on perspective and intensity
        const maxDistance = 300 * config.connectionDistance * (1000 / (1000 + Math.abs(node.z)));
        
        if (distance < maxDistance) {
          const strength = 1 - distance / maxDistance;
          
          // Draw connection with depth-based opacity
          const depthFactor = Math.min(1, Math.max(0.2, 1 - Math.abs(node.z) / 1000));
          
          // Data flowing effect
          if ((node.type === 'aiAgent' && other.type === 'transaction') ||
              (node.type === 'validator' && other.type === 'smartContract')) {
            drawDataFlow(node, other, strength, depthFactor);
          } else {
            // Standard connection
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            
            // Enhanced connection styling based on node types
            if (node.type === 'validator' || other.type === 'validator') {
              ctx.strokeStyle = `rgba(0, 255, 255, ${strength * 0.5 * depthFactor})`;
              ctx.lineWidth = 2 * depthFactor;
              ctx.setLineDash([5, 5]);
            } else if (node.type === 'aiAgent' || other.type === 'aiAgent') {
              ctx.strokeStyle = `rgba(255, 51, 102, ${strength * 0.4 * depthFactor})`;
              ctx.lineWidth = 1.5 * depthFactor;
              ctx.setLineDash([2, 3]);
            } else {
              ctx.strokeStyle = `rgba(0, 255, 140, ${strength * 0.3 * depthFactor})`;
              ctx.lineWidth = 1 * depthFactor;
              ctx.setLineDash([]);
            }
            
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      });
    };
    
    // Draw data flow animation along connections
    const drawDataFlow = (from: ParticleNode, to: ParticleNode, strength: number, depthFactor: number) => {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Base connection
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      
      const connectionColor = from.type === 'aiAgent' ? 
        `rgba(255, 51, 102, ${strength * 0.3 * depthFactor})` : 
        `rgba(0, 255, 255, ${strength * 0.3 * depthFactor})`;
      
      ctx.strokeStyle = connectionColor;
      ctx.lineWidth = 1 * depthFactor;
      ctx.stroke();
      
      // Data packets moving along the connection
      const packetCount = Math.floor(distance / 40) + 1;
      
      for (let i = 0; i < packetCount; i++) {
        const progress = (frameCount.current * 0.02 + i / packetCount) % 1;
        const x = from.x + dx * progress;
        const y = from.y + dy * progress;
        
        const packetSize = 2 * depthFactor;
        
        ctx.beginPath();
        ctx.arc(x, y, packetSize, 0, Math.PI * 2);
        
        ctx.shadowColor = from.type === 'aiAgent' ? '#ff3366' : '#00ffff';
        ctx.shadowBlur = 10;
        
        ctx.fillStyle = from.type === 'aiAgent' ? '#ff3366' : '#00ffff';
        ctx.fill();
        
        ctx.shadowBlur = 0;
      }
    };

    // Enhanced node color handling with status effects
    const getNodeColors = (node: ParticleNode): ColorPalette => {
      const baseColors = colors[node.type];
      switch (node.status) {
        case 'mining':
          return {
            ...baseColors,
            primary: '#ff9900',
            pulse: '#ff990033',
            gradient: ['#ffaa00', '#ff8800', '#ff7700']
          };
        case 'processed':
          return {
            ...baseColors,
            primary: '#00ff8c',
            pulse: '#00ff8c33',
            gradient: ['#00ff99', '#00ff88', '#00ee77']
          };
        case 'deciding':
          return {
            ...baseColors,
            primary: '#ff00ff',
            pulse: '#ff00ff33',
            gradient: ['#ff33ff', '#ff00ff', '#ee00ee']
          };
        case 'confirmed':
          return {
            ...baseColors,
            primary: '#00ffff',
            pulse: '#00ffff33',
            gradient: ['#33ffff', '#00ffff', '#00eeee']
          };
        default:
          return baseColors;
      }
    };

    // Physics simulation
    const applyPhysics = (nodes: ParticleNode[], deltaTime: number): void => {
      // Handle mouse/touch interaction
      if (interactionZone.current.active) {
        interactionZone.current.strength = Math.min(1, interactionZone.current.strength + deltaTime * 2);
      } else {
        interactionZone.current.strength = Math.max(0, interactionZone.current.strength - deltaTime * 2);
      }
      
      if (interactionZone.current.strength > 0) {
        const attractRadius = 200;
        const attractStrength = 300 * interactionZone.current.strength;
        
        nodes.forEach(node => {
          const dx = interactionZone.current.x - node.x;  // Direction FROM node TO mouse (attraction)
          const dy = interactionZone.current.y - node.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < attractRadius) {
            const force = (1 - distance / attractRadius) * attractStrength * deltaTime;
            
            // Apply force directly using the dx/dy vector (scaled appropriately)
            node.baseX += (dx / distance) * force * 0.1;
            node.baseY += (dy / distance) * force * 0.1;
            
            // Gradually return to original position
            node.baseX *= 0.99;
            node.baseY *= 0.99;
          }
        });
      }
    };

    // Optimized animation loop with time delta and reduced motion support
    const animate = (timestamp: number): void => {
      if (!isActive.current) {
        // Throttle animation when not visible
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      
      const deltaTime = (timestamp - (lastTime.current || timestamp)) / 1000;
      lastTime.current = timestamp;
      
      // Skip frames for reduced motion or performance
      if (reducedMotion) {
        frameSkipCount.current++;
        if (frameSkipCount.current < 3) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        frameSkipCount.current = 0;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount.current++;
      
      // Apply physics to nodes
      applyPhysics(nodesRef.current, deltaTime);

      // Update and draw nodes
      nodesRef.current.forEach((node, index) => {
        // Age-based particle lifecycle
        node.age += deltaTime * 1000;
        if (node.age > node.maxAge) {
          // Replace old particle with new one
          const newNode = createNode(node.type);
          nodesRef.current[index] = newNode;
          return;
        }
        
        // 3D rotation with improved motion (reduced for reduced motion)
        const animationMultiplier = reducedMotion ? 0.3 : 1;
        const time = frameCount.current * 0.0005 * node.speed * animationMultiplier;
        
        // Apply two-axis rotation for more complex motion
        const cosX = Math.cos(time * 0.7);
        const sinX = Math.sin(time * 0.7);
        const cosY = Math.cos(time);
        const sinY = Math.sin(time);
        
        // Apply combined rotation
        const rotX = node.baseX * cosY - node.baseZ * sinY;
        const rotZ = node.baseX * sinY + node.baseZ * cosY;
        
        node.z = rotZ * cosX - node.baseY * sinX;
        node.y = rotZ * sinX + node.baseY * cosX;
        node.x = rotX;

        // Apply perspective projection
        const perspective = 1200; // Adjusted for better 3D effect
        const scale = perspective / (perspective + node.z);
        
        // Transform to screen coordinates
        node.x = canvas.width/2 + node.x * scale;
        node.y = canvas.height/2 + node.y * scale;

        // Scale size based on z-position
        const baseSize = node.type === 'validator' ? 12 : 
                        node.type === 'smartContract' ? 8 : 
                        node.type === 'aiAgent' ? 10 : 4;
        node.size = baseSize * scale;

        // Update pulse phase
        node.pulsePhase += deltaTime * (0.5 + node.energy * 2);
        
        // Update trail
        if (!reducedMotion) {
          const currentPos = { x: node.x, y: node.y, z: node.z };
          const distance = Math.sqrt(
            Math.pow(currentPos.x - node.lastPosition.x, 2) +
            Math.pow(currentPos.y - node.lastPosition.y, 2) +
            Math.pow(currentPos.z - node.lastPosition.z, 2)
          );
          
          // Add trail point if moved enough
          if (distance > 5) {
            node.trail.unshift({ ...currentPos, opacity: 1 });
            node.lastPosition = currentPos;
            
            // Limit trail length and fade
            if (node.trail.length > 8) {
              node.trail.pop();
            }
            
            // Fade trail points
            node.trail.forEach((point, index) => {
              point.opacity = Math.max(0, 1 - index * 0.15);
            });
          }
        }
      });

      // Sort nodes by z-index for proper rendering
      const sortedNodes = [...nodesRef.current].sort((a, b) => b.z - a.z);

      // Draw connections first
      sortedNodes.forEach(node => {
        drawConnections(node, sortedNodes);
      });

      // Draw trails
      if (!reducedMotion) {
        sortedNodes.forEach(drawTrail);
      }

      // Draw nodes on top
      sortedNodes.forEach(drawNode);

      // Draw cursor effect
      if (!reducedMotion && interactionZone.current.strength > 0) {
        drawCursorEffect();
      }

      // Process nodes through stages with probabilistic transitions
      nodesRef.current.forEach(node => {
        // Progress through stages with varying probabilities
        const transitionChance = deltaTime * (node.type === 'transaction' ? 0.5 : 
                                           node.type === 'aiAgent' ? 0.3 : 0.2);
                                           
        if (Math.random() < transitionChance) {
          switch (node.status) {
            case 'pending':
              node.status = 'mining';
              break;
            case 'mining':
              node.status = 'processed';
              break;
            case 'processed':
              node.status = 'deciding';
              break;
            case 'deciding':
              node.status = 'confirmed';
              break;
            case 'confirmed':
              // Reset the cycle
              node.status = 'pending';
              node.data.hash = generateHash(); // New transaction
              
              // For AI agents, update their properties
              if (node.type === 'aiAgent' && node.data.confidence !== undefined) {
                node.data.confidence = 0.5 + Math.random() * 0.5;
                node.data.profitLoss = (Math.random() * 2 - 1) * 100;
                node.data.status = (['training', 'analyzing', 'predicting'] as const)[Math.floor(Math.random() * 3)];
              }
              break;
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, [colors, enhanced, reducedMotion, intensity, theme]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-auto ${
        theme === 'light' ? 'bg-gray-50' : 'bg-black'
      }`}
      style={{ 
        opacity: theme === 'neon' ? 0.95 : 0.85,
        transition: 'opacity 0.3s ease-in-out'
      }}
      aria-label="Interactive particle background animation"
      role="img"
    />
  );
};

export default ParticleBackground;